import { ChangeEvent, useState } from "react";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatNumber } from "../../utils";
import ConfirmModal from "../ConfirmModal";
import { EMPTY_TEXT } from "./constants";
import { CloseSummary } from "./monthlyExpenseDraft";
import { formatSignedAmount } from "./monthlyExpenseLayout";

export const ACKNOWLEDGE_TEXT = "ตรวจยอดแล้ว และเข้าใจว่าแก้ไขไม่ได้หลังส่ง";

interface CloseMonthDialogProps {
  open: boolean;
  summary: CloseSummary;
  monthTitle: string;
  estimateTitle: string;
  averageSales: number | null;
  averageManager: number | null;
  isClosing: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const formatOrEmpty = (value: number | null) => (value === null ? EMPTY_TEXT : formatNumber(value));

/**
 * ส่งและปิดเดือน is one-way, so it is confirmed on a summary (branches, documents not counted,
 * the month's total, extras, the adjustments and their impact, the estimate) and only after
 * ticking ตรวจยอดแล้ว. Mount it anew (a key) for each opening, so the tick starts empty.
 */
export default function CloseMonthDialog({
  open,
  summary,
  monthTitle,
  estimateTitle,
  averageSales,
  averageManager,
  isClosing,
  onClose,
  onConfirm,
}: Readonly<CloseMonthDialogProps>) {
  const [isAcknowledged, setAcknowledged] = useState(false);
  const handleAcknowledge = (event: ChangeEvent<HTMLInputElement>) => setAcknowledged(event.target.checked);
  const unapprovedText =
    summary.unapprovedDayCount > 0
      ? `${summary.unapprovedBranchCount} สาขา (${summary.unapprovedDayCount} วัน) — จะไม่ถูกนับ`
      : "ครบทุกสาขา";
  const lines: [string, string][] = [
    ["สาขา", `${summary.branchCount} สาขา · ${summary.routeCount} สาย`],
    ["เอกสารรายวันที่ยังไม่อนุมัติ", unapprovedText],
    [`ค่าใช้จ่ายรวมเดือน${monthTitle} (ยอดจริง)`, `${formatNumber(summary.monthTotal)} บาท`],
    ["ค่าใช้จ่ายเพิ่มเติม", `${summary.extraCount} รายการ · ${formatNumber(summary.extraTotal)} บาท`],
    [
      "ช่องประมาณการที่ปรับ",
      `${summary.adjustedCount} ช่อง · ผลต่อเดือน ${formatSignedAmount(summary.monthlyImpact)} บาท`,
    ],
    [
      `${estimateTitle} เฉลี่ยต่อสายต่อวัน`,
      `Sales ${formatOrEmpty(averageSales)} · Manager ${formatOrEmpty(averageManager)}`,
    ],
  ];

  return (
    <ConfirmModal
      open={open}
      title={`ส่งและปิดเดือน${monthTitle}`}
      cancelLabel="กลับไปตรวจ"
      confirmLabel="ส่งและปิดเดือน"
      confirmDisabled={!isAcknowledged}
      loading={isClosing}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <Stack gap={1.5}>
        <Alert severity="warning">
          <b>ส่งแล้วแก้ไขหรือเปิดเดือนกลับไม่ได้</b>
          <br />
          เอกสารรายวันของเดือน{monthTitle} ทุกใบจะถูกล็อก และยอดประมาณการจะส่งให้ทุกสาขาทันที
        </Alert>
        <Stack>
          {lines.map(([label, value]) => (
            <Stack
              key={label}
              direction="row"
              justifyContent="space-between"
              gap={2}
              sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" textAlign="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <FormControlLabel
          control={<Checkbox checked={isAcknowledged} onChange={handleAcknowledge} />}
          label={ACKNOWLEDGE_TEXT}
        />
      </Stack>
    </ConfirmModal>
  );
}
